/**
 * THE ICON REGISTRY — every button, nav item, stat, currency and status in the
 * game has a NAMED SLOT here, whether or not its painted art exists yet.
 *
 * ── WHY SLOTS AND NOT FILE NAMES ────────────────────────────────────────────
 * The owner's ask is "icons for everything, for every button, for every thing"
 * and, after this pass, "we will start generation of those in higgsfield". A
 * screen that types a file name can only ever show art that already exists; a
 * screen that names a SLOT shows whatever is the best thing the build has for
 * that meaning today, and silently upgrades the day the art lands.
 *
 * Resolution, per slot, in order:
 *
 *   1. `art` — one of the painted icons already ingested into `public/ui/`
 *      (`UI_ICONS` in `icon.ts`). Used as-is.
 *   2. `/ui/slots/<slot>.png` — the path `docs/ui/HIGGSFIELD-GENERATION-PLAN.md`
 *      tells the generation run to write. Probed with an `<img>`; a file that
 *      is there simply appears. NO CODE CHANGE is needed when it lands — that
 *      is the whole contract of this module.
 *   3. The PLACEHOLDER — a sticker-style medallion drawn in SVG: a toned disc,
 *      a heavy dark outline and a white pictogram, the same visual grammar as
 *      the painted set (thick outline, flat fill, one gloss). It is a stand-in,
 *      consistent across every missing slot, never a Unicode glyph and never a
 *      currency code.
 *
 * ── THE PROBE IS PAID ONCE PER SESSION ──────────────────────────────────────
 * A missing slot file costs one failed request. The failure is remembered in
 * memory (and in sessionStorage, guarded), so the next render of that slot goes
 * straight to the placeholder instead of re-asking the server on every screen
 * rebuild. A dev server answering a missing file with `index.html` and a 200
 * fails to DECODE, which fires `error` exactly like a 404 — the trap
 * `icon.ts` documents — so both land on the placeholder.
 *
 * ── EVERY ICON IS DECORATIVE ────────────────────────────────────────────────
 * `aria-hidden` always. The accessible name stays on the control that carries
 * the icon; an icon-only control must pass an `aria-label`.
 */
import { hasIcon, UI_ICON_BASE, type UiIconId } from './icon';

/* ══════════════════════════════════════════════════════════ pictograms ══ */

/**
 * 24×24 stroke pictograms for the placeholder medallion. Drawn white over a
 * dark under-stroke, so they read on every disc tone. Kept deliberately few:
 * a placeholder vocabulary that grows per slot stops being a placeholder.
 */
export const PICTOGRAMS = Object.freeze({
  blades: 'M5 19 15 9M9 5l10 10M15 9l2-4 2 2-4 2M9 15l-4 2 2 2 2-4',
  cards: 'M7 4h9a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM9 8h5M9 12h5',
  stall: 'M4 9h16l-2-4H6zM5 9v10h14V9M9 19v-5h6v5',
  keep: 'M6 20V9l2-2 2 2 2-2 2 2 2-2 2 2v11zM10 20v-4h4v4',
  beacon: 'M12 3c2 3 3 4 3 6a3 3 0 0 1-6 0c0-2 1-3 3-6zM8 21l4-9 4 9M9 17h6',
  podium: 'M4 20v-6h5v6M9 20V9h6v11M15 20v-8h5v8',
  bust: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 20c1-4 4-6 7-6s6 2 7 6',
  gear: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1',
  burger: 'M5 7h14M5 12h14M5 17h14',
  chat: 'M5 6h14v9H10l-4 3v-3H5z',
  back: 'M15 5l-7 7 7 7',
  next: 'M9 5l7 7-7 7',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12.5l4.5 4.5L19 7',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  coin: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 8v8M9.5 10h4a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h4',
  gem: 'M7 4h10l4 5-9 11L3 9zM3 9h18M9 4l3 16 3-16',
  lock: 'M7 11h10v9H7zM9 11V8a3 3 0 0 1 6 0v3',
  unlock: 'M7 11h10v9H7zM9 11V8a3 3 0 0 1 6 0',
  info: 'M12 10v7M12 6.5v.5',
  help: 'M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14M12 17.5v.5',
  warn: 'M12 4 3 20h18zM12 10v4M12 17v.5',
  star: 'M12 4l2.4 5 5.6.6-4.2 3.8 1.2 5.6L12 16.2 7 19l1.2-5.6L4 9.6 9.6 9z',
  bell: 'M6 16h12l-1.5-2V10a4.5 4.5 0 0 0-9 0v4zM10 19h4',
  speaker: 'M5 10h3l4-4v12l-4-4H5zM15 9a4 4 0 0 1 0 6M17.5 7a7 7 0 0 1 0 10',
  mute: 'M5 10h3l4-4v12l-4-4H5zM15 9l5 6M20 9l-5 6',
  music: 'M9 17V6l10-2v11M9 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM19 15a2 2 0 1 1-4 0 2 2 0 0 1 4 0z',
  globe: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM4 12h16M12 4c2.5 2.5 2.5 13.5 0 16M12 4c-2.5 2.5-2.5 13.5 0 16',
  trash: 'M5 7h14M10 7V5h4v2M7 7l1 13h8l1-13',
  pencil: 'M5 19l1-4L16 5l3 3L9 18zM14 7l3 3',
  share: 'M7 12a2 2 0 1 1 0 .1M17 6a2 2 0 1 1 0 .1M17 18a2 2 0 1 1 0 .1M9 11l6-4M9 13l6 4',
  search: 'M11 5a6 6 0 1 0 0 12 6 6 0 0 0 0-12zM15.5 15.5 20 20',
  refresh: 'M19 8a7 7 0 1 0 1 5M19 4v4h-4',
  eye: 'M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6zM12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
  play: 'M8 5v14l11-7z',
  flag: 'M6 21V4M6 5h11l-2 4 2 4H6',
  gift: 'M4 10h16v3H4zM5 13h14v7H5zM12 10v10M12 10c-2-4-6-3-4-1M12 10c2-4 6-3 4-1',
  hourglass: 'M7 4h10M7 20h10M8 4c0 5 8 5 8 8s-8 3-8 8M16 4c0 5-8 5-8 8',
  crown: 'M4 17l1-9 4 4 3-6 3 6 4-4 1 9zM5 20h14',
  shield: 'M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z',
  mail: 'M4 6h16v12H4zM4 7l8 6 8-6',
  users: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 19c.8-3 3-4.5 6-4.5s5.2 1.5 6 4.5M16 5a3 3 0 0 1 0 6M18 14.5c1.6.6 2.6 2 3 4.5',
  exit: 'M14 4H6v16h8M10 12h10M17 9l3 3-3 3',
  copy: 'M8 8h11v12H8zM5 16V4h11',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  upload: 'M12 20V9M7 14l5-5 5 5M5 4h14',
  home: 'M4 11l8-7 8 7M6 10v10h12V10',
  filter: 'M4 5h16l-6 8v6l-4-2v-4z',
  sort: 'M8 4v16M4 8l4-4 4 4M16 20V4M12 16l4 4 4-4',
  cart: 'M4 5h2l2 10h10l2-7H7M9 19.5v.5M17 19.5v.5',
  cash: 'M3 7h18v10H3zM12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM6 10v4M18 10v4',
  card: 'M3 6h18v12H3zM3 10h18M6 15h4',
  bank: 'M3 9l9-5 9 5M5 10v7M9.5 10v7M14.5 10v7M19 10v7M3 20h18',
  swap: 'M5 8h13l-3-3M19 16H6l3 3',
  chest: 'M4 10h16v9H4zM4 10a8 5 0 0 1 16 0M11 13h2v3h-2z',
  scroll: 'M7 4h11v13a3 3 0 0 1-3 3H6a2 2 0 0 1-2-2v-1h11M7 4a2 2 0 0 0-2 2v11M10 8h5M10 11h5',
  bolt: 'M13 3 5 13h6l-1 8 8-10h-6z',
  dice: 'M5 5h14v14H5zM9 9v.5M15 9v.5M12 12v.5M9 15v.5M15 15v.5',
  trophy: 'M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 20h8',
  link: 'M10 14a4 4 0 0 0 6 0l3-3a4 4 0 0 0-6-6l-1 1M14 10a4 4 0 0 0-6 0l-3 3a4 4 0 0 0 6 6l1-1',
  ban: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM6.5 6.5l11 11',
  emote: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM9 10v.5M15 10v.5M8.5 14c1.8 2 5.2 2 7 0',
  replay: 'M5 12a7 7 0 1 0 2-5M5 4v4h4M11 9v6l4-3z',
  text: 'M5 6h14M12 6v13M9 19h6',
  palette: 'M12 4a8 8 0 0 0 0 16c1.5 0 2-1 1.5-2s0-2 1.5-2H18a2 2 0 0 0 2-2 8 8 0 0 0-8-10zM8 11v.5M11 8v.5M15 8.5v.5',
  hand: 'M8 13V6a1.5 1.5 0 0 1 3 0v5M11 11V5a1.5 1.5 0 0 1 3 0v6M14 11V7a1.5 1.5 0 0 1 3 0v7a6 6 0 0 1-6 6h-1a5 5 0 0 1-4-2l-3-4a1.5 1.5 0 0 1 2.5-1.5L8 14',
  doc: 'M7 3h7l4 4v14H7zM14 3v4h4M10 12h5M10 16h5',
  sword: 'M5 19l3-1 10-10 1-4-4 1L5 15zM5 19l-1 1M8 15l-3-3',
} as const);

export type Pictogram = keyof typeof PICTOGRAMS;

/* ═══════════════════════════════════════════════════════════ the slots ══ */

/** What a slot is for; drives the placeholder's disc tone and the plan's batch. */
export type SlotGroup =
  | 'nav'
  | 'header'
  | 'action'
  | 'currency'
  | 'stat'
  | 'status'
  | 'social'
  | 'settings'
  | 'match'
  | 'commerce';

export interface IconSlotSpec {
  readonly group: SlotGroup;
  /** Painted art already shipped in `public/ui/`, if any. */
  readonly art?: UiIconId;
  /** Placeholder pictogram while no art exists. */
  readonly glyph: Pictogram;
  /** What the icon shows — the subject line of its generation prompt. */
  readonly subject: string;
}

/** Shorthand so the table below stays one line per slot. */
const s = (group: SlotGroup, glyph: Pictogram, subject: string, art?: UiIconId): IconSlotSpec =>
  art === undefined ? Object.freeze({ group, glyph, subject }) : Object.freeze({ group, glyph, subject, art });

/**
 * EVERY SLOT IN THE GAME. Ids are kebab-case and stable: they are file names in
 * `public/ui/slots/` and rows in the generation plan, so renaming one orphans
 * its art. Add, never rename.
 */
export const ICON_SLOTS = Object.freeze({
  // ── navigation rail ────────────────────────────────────────────────────
  'nav-battle': s('nav', 'blades', 'crossed blunt fantasy blades over a small round shield', 'nav-battle'),
  'nav-cards': s('nav', 'cards', 'three blank cards fanned out', 'nav-cards'),
  'nav-shop': s('nav', 'stall', 'a striped market stall awning over a wooden counter', 'nav-shop'),
  'nav-house': s('nav', 'keep', 'a small stone keep with a blank banner', 'nav-house'),
  'nav-events': s('nav', 'beacon', 'a lit signal beacon brazier', 'nav-events'),
  'nav-leaderboard': s('nav', 'podium', 'a three-step winners podium', 'nav-leaderboard'),
  'nav-profile': s('nav', 'bust', 'a hero bust silhouette on a round plinth', 'nav-profile'),
  'nav-settings': s('nav', 'gear', 'a chunky bronze cog', 'nav-settings'),
  'nav-world': s('nav', 'globe', 'a small painted world globe with a brass ring'),
  'nav-modes': s('nav', 'dice', 'a carved stone die beside a small banner'),
  'nav-social': s('nav', 'users', 'two hero busts side by side'),
  'nav-wallet': s('nav', 'cash', 'a leather coin purse with a brass clasp'),
  'nav-marketplace': s('nav', 'swap', 'two hands exchanging a card over a market table'),
  'nav-wardrobe': s('nav', 'palette', 'a hanging cloak on a wooden mannequin'),
  'nav-crates': s('nav', 'chest', 'a closed iron-banded treasure chest'),
  // ── header chrome ──────────────────────────────────────────────────────
  'header-menu': s('header', 'burger', 'three stacked rounded bronze bars'),
  'header-settings': s('header', 'gear', 'a chunky bronze cog', 'nav-settings'),
  'header-chat': s('header', 'chat', 'a parchment speech bubble with a wax seal'),
  'header-back': s('header', 'back', 'a chunky left-pointing arrow carved from wood'),
  'header-close': s('header', 'close', 'a bold red X made of two crossed planks'),
  'header-notifications': s('header', 'bell', 'a small brass hand bell'),
  // ── currencies ─────────────────────────────────────────────────────────
  'currency-gold': s('currency', 'coin', 'a stack of gold coins', 'gold'),
  'currency-shards': s('currency', 'gem', 'a cluster of glowing blue crystal shards', 'shards'),
  'currency-sigils': s('currency', 'shield', 'a wax-sealed rank sigil', 'sigils'),
  'currency-cash': s('currency', 'cash', 'a folded banknote with a gold band'),
  'currency-xp': s('currency', 'star', 'a glowing experience mote', 'xp-mote'),
  'currency-dust': s('currency', 'gem', 'a bowl of shimmering dust motes', 'dust-motes-bowl'),
  'currency-marks': s('currency', 'flag', 'a stack of three bronze house tokens stamped with a keep'),
  'currency-writs': s('currency', 'scroll', 'a rolled parchment writ with a red wax seal'),
  // ── universal actions ──────────────────────────────────────────────────
  'action-play': s('action', 'play', 'a bold forward-pointing triangle on a round shield'),
  'action-confirm': s('action', 'check', 'a bold green tick', 'quest-complete-tick'),
  'action-cancel': s('action', 'close', 'a bold red X'),
  'action-add': s('action', 'plus', 'a bold gold plus sign on a round boss'),
  'action-remove': s('action', 'minus', 'a bold minus sign on a round boss'),
  'action-edit': s('action', 'pencil', 'a quill pen over a parchment corner'),
  'action-delete': s('action', 'trash', 'a cracked stone urn tipping over'),
  'action-share': s('action', 'share', 'a carrier pigeon holding a small scroll'),
  'action-copy': s('action', 'copy', 'two overlapping parchment sheets'),
  'action-search': s('action', 'search', 'a brass magnifying glass'),
  'action-filter': s('action', 'filter', 'a brass funnel'),
  'action-sort': s('action', 'sort', 'two opposing chunky arrows'),
  'action-refresh': s('action', 'refresh', 'a curved arrow forming a circle'),
  'action-reroll': s('action', 'refresh', 'two swapping arrows over a quest scroll', 'quest-reroll-swap'),
  'action-info': s('action', 'info', 'a round blue info medallion'),
  'action-help': s('action', 'help', 'a round parchment medallion with a question curl'),
  'action-next': s('action', 'next', 'a chunky right-pointing arrow carved from wood'),
  'action-prev': s('action', 'back', 'a chunky left-pointing arrow carved from wood'),
  'action-expand': s('action', 'plus', 'a downward chevron on a round boss'),
  'action-download': s('action', 'download', 'a down arrow into an open chest'),
  'action-upload': s('action', 'upload', 'an up arrow out of an open chest'),
  'action-external': s('action', 'link', 'two linked iron chain rings'),
  'action-sign-out': s('action', 'exit', 'an open wooden door with an arrow leaving it'),
  'action-send': s('action', 'mail', 'a paper plane folded from parchment'),
  'action-claim': s('action', 'gift', 'a ribboned reward bundle', 'reward-bundle'),
  'action-upgrade': s('action', 'bolt', 'a card with an upward glowing arrow', 'card-level-up'),
  'action-retry': s('action', 'refresh', 'a curved arrow forming a circle'),
  'action-home': s('action', 'home', 'a small cottage with a red roof'),
  // ── commerce ───────────────────────────────────────────────────────────
  'commerce-buy': s('commerce', 'cart', 'a wicker basket with a gold coin on top'),
  'commerce-sell': s('commerce', 'swap', 'a hand passing a card across a table'),
  'commerce-withdraw': s('commerce', 'bank', 'a stone bank facade with an outgoing coin'),
  'commerce-deposit': s('commerce', 'bank', 'a stone bank facade with an incoming coin'),
  'commerce-card-payment': s('commerce', 'card', 'a plain blank payment card, no logos'),
  'commerce-bank-transfer': s('commerce', 'bank', 'a stone bank facade with columns'),
  'commerce-price-plate': s('commerce', 'coin', 'a blank brass price plate', 'price-plate-blank'),
  'commerce-gift': s('commerce', 'gift', 'a wrapped present with a red ribbon'),
  'commerce-offer': s('commerce', 'star', 'a starburst sale tag, blank'),
  'commerce-premium': s('commerce', 'crown', 'a premium track crest', 'track-premium-mark'),
  'commerce-crate': s('commerce', 'chest', 'an iron-banded crate', 'crate-warded-cache'),
  'commerce-invoice': s('commerce', 'doc', 'a rolled parchment receipt with a wax seal'),
  // ── stats ──────────────────────────────────────────────────────────────
  'stat-rank': s('stat', 'shield', 'a rank seal medallion', 'sigil-rank-seal'),
  'stat-trophy': s('stat', 'trophy', 'a gold trophy cup'),
  'stat-crown': s('stat', 'crown', 'a gold three-point crown'),
  'stat-wins': s('stat', 'sword', 'a single upright sword with a laurel'),
  'stat-level': s('stat', 'star', 'a gold star with a number plate, plate blank'),
  'stat-mana': s('stat', 'gem', 'a mana cost pip', 'cost-pip-mana'),
  'stat-cards-owned': s('stat', 'cards', 'a stack of card copies', 'card-copies-stack'),
  'stat-timer': s('stat', 'hourglass', 'a brass hourglass', 'timer-hourglass'),
  'stat-streak': s('stat', 'bolt', 'a flame-wrapped lightning bolt'),
  'stat-season': s('stat', 'flag', 'a pennant on a short pole'),
  'stat-rank-up': s('stat', 'upload', 'a bold green arrow climbing over a rank seal'),
  'stat-rank-down': s('stat', 'download', 'a bold red arrow dropping under a rank seal'),
  'stat-rank-hold': s('stat', 'minus', 'a level bar resting on a rank seal'),
  // ── status ─────────────────────────────────────────────────────────────
  'status-success': s('status', 'check', 'a bold green tick in a round badge'),
  'status-error': s('status', 'close', 'a bold red X in a round badge'),
  'status-warning': s('status', 'warn', 'an amber triangle warning sign'),
  'status-info': s('status', 'info', 'a round blue info badge'),
  'status-locked': s('status', 'lock', 'a heavy iron padlock', 'warpath-node-locked'),
  'status-unlocked': s('status', 'unlock', 'an open iron padlock'),
  'status-claimed': s('status', 'check', 'a claimed warpath node', 'warpath-node-claimed'),
  'status-new': s('status', 'star', 'a small burst star badge'),
  'status-empty': s('status', 'chest', 'an empty open wooden crate', 'crate-slot-empty'),
  'status-loading': s('status', 'hourglass', 'a brass hourglass mid-flip', 'timer-hourglass'),
  'status-offline': s('status', 'ban', 'a cut rope bridge'),
  // ── social ─────────────────────────────────────────────────────────────
  'social-friends': s('social', 'users', 'two hero busts side by side', 'social-friends'),
  'social-blocked': s('social', 'ban', 'a blocked shield', 'social-blocked'),
  'social-spectate': s('social', 'eye', 'a spyglass', 'social-spectate'),
  'social-invite': s('social', 'mail', 'a sealed invitation letter'),
  'social-replay': s('social', 'replay', 'a film reel wrapped in a ribbon'),
  'social-emote': s('social', 'emote', 'the emote wheel', 'tile-emote-wheel'),
  'social-leader': s('social', 'crown', 'a house leader crest', 'role-leader'),
  'social-officer': s('social', 'shield', 'a house officer crest', 'role-officer'),
  'social-donate': s('social', 'gift', 'a donation chest', 'house-donate'),
  'social-war': s('social', 'flag', 'a war standard', 'house-war-standard'),
  // ── settings ───────────────────────────────────────────────────────────
  'settings-sound': s('settings', 'speaker', 'a brass horn speaker'),
  'settings-mute': s('settings', 'mute', 'a brass horn speaker with a red X'),
  'settings-music': s('settings', 'music', 'a lute with two floating notes'),
  'settings-language': s('settings', 'globe', 'a world globe with a speech bubble'),
  'settings-currency': s('settings', 'coin', 'three different coins fanned out'),
  'settings-accessibility': s('settings', 'eye', 'an eye with a soft halo'),
  'settings-display': s('settings', 'palette', 'a painter palette'),
  'settings-account': s('settings', 'bust', 'a hero bust with a key'),
  'settings-legal': s('settings', 'scroll', 'a rolled legal scroll with a seal'),
  'settings-left-hand': s('settings', 'hand', 'an open left hand'),
  // ── match HUD ──────────────────────────────────────────────────────────
  'match-emote': s('match', 'emote', 'the emote wheel', 'tile-emote-wheel'),
  'match-mute': s('match', 'mute', 'a speaker with a red X'),
  'match-unmute': s('match', 'speaker', 'a brass horn speaker'),
  'match-quick-chat': s('match', 'chat', 'a parchment speech bubble'),
  'match-surrender': s('match', 'flag', 'a white flag on a short pole'),
  'match-pause': s('match', 'hourglass', 'a paused hourglass'),
  'match-elixir': s('match', 'gem', 'a mana pip', 'cost-pip-mana'),
  'match-deploy': s('match', 'bolt', 'a deploy burst', 'tile-deploy-vfx'),
  'match-leave': s('match', 'exit', 'an open wooden arena gate with a small white flag'),
  // ── the living lobby (docs/ui/LIVING-LOBBY.md): presence, ticker, inbox, goals
  'live-online': s('status', 'users', 'a glowing green beacon crystal over two tiny hero busts'),
  'live-ticker-win': s('stat', 'sword', 'two crossed swords inside a small laurel wreath'),
  'live-ticker-rank-up': s('stat', 'upload', 'a rank seal with a rising golden arrow and sparkles'),
  'live-ticker-crate': s('commerce', 'chest', 'an open crate with light and one card bursting out'),
  'inbox-friend': s('social', 'users', 'two armoured hands clasped in a handshake'),
  'inbox-gift': s('commerce', 'gift', 'a ribboned gift box with a small blank tag'),
  'inbox-quest': s('action', 'check', 'a quest scroll stamped with a green tick seal'),
  'inbox-warpath': s('stat', 'star', 'a warpath milestone stone with a glowing star on top'),
  'inbox-crate': s('commerce', 'chest', 'an unlocked iron-banded crate with light spilling from the lid'),
  'inbox-news': s('header', 'scroll', 'a rolled news scroll with a wax seal and a tiny pennant'),
  'inbox-season': s('stat', 'flag', 'a tall season banner on a pole with a star emblem'),
  'goal-quest': s('action', 'scroll', 'an open quest scroll with a quill resting across it'),
  'goal-warpath': s('stat', 'flag', 'a wooden road signpost with a pennant at the top'),
  'goal-crate': s('commerce', 'hourglass', 'a small crate with a brass hourglass sitting on its lid'),
  // ── the landing page (engine/landing): the picture tiles that replaced its prose
  'landing-lanes': s('stat', 'swap', 'a small round floating grass island split by a river with two stone bridges'),
  'landing-realtime': s('stat', 'bolt', 'a golden lightning bolt striking across a brass stopwatch'),
  'landing-fair': s('status', 'minus', 'a brass balance scale resting perfectly level'),
  'landing-spell': s('stat', 'scroll', 'a parchment spell scroll with a swirl of teal and gold magic'),
  'landing-tower': s('stat', 'keep', 'a round stone watchtower with a red conical roof and a pennant'),
  'landing-deploy': s('action', 'cards', 'a blue-backed card standing where it landed on a disc of sandy ground'),
  'landing-scout': s('action', 'eye', 'an extended brass spyglass with a glinting lens'),
  'landing-sigils': s('stat', 'shield', 'three gold victory medallions fanned out, the middle one raised'),
  // ── package offers (engine/promo): the pop-up's ribbon and the bonus badge's burst
  'promo-ribbon': s('commerce', 'flag', 'a curled red-and-gold sale ribbon banner with a small star, blank'),
  'promo-burst': s('commerce', 'star', 'a bright gold starburst sticker with a white rim, blank'),
  // ── emotes: the stand-in for an emote with no hand-made `public/emotes/<id>.png`.
  //    The id after `emote-` is the wire `EMOTE_IDS` slug, so the HUD resolves
  //    `emote-${id}` with no lookup table. Append-only, like the wire.
  'emote-salute': s('match', 'hand', 'a raised armoured hand giving a salute'),
  'emote-well-played': s('match', 'star', 'two clapping gauntlets under a small star'),
  'emote-rally': s('match', 'flag', 'a raised rally banner on a spear'),
  'emote-thinking': s('match', 'help', 'a curling thought cloud over a chin-scratching hand'),
  'emote-oops': s('match', 'warn', 'a tipped-over helmet with a sweat drop'),
  'emote-thanks': s('match', 'gift', 'a small bowing ribbon over two cupped hands'),
  'emote-good-luck': s('match', 'star', 'a four-leaf clover charm on a cord'),
  'emote-so-close': s('match', 'hourglass', 'a cracked hourglass with one grain left'),
  'emote-well-held': s('match', 'shield', 'a dented round shield still standing'),
  'emote-nice-try': s('match', 'check', 'a thumbs-up gauntlet'),
  'emote-taunt': s('match', 'sword', 'a sword tip wagging cheekily'),
  'emote-laugh': s('match', 'emote', 'a laughing round mask'),
  'emote-steady': s('match', 'minus', 'a level balance scale'),
  'emote-wow': s('match', 'bolt', 'a burst of sparks around a wide-eyed mask'),
} as const satisfies Record<string, IconSlotSpec>);

export type IconSlot = keyof typeof ICON_SLOTS;

/** Every slot id, sorted — for the generation plan and the coverage test. */
export const ICON_SLOT_IDS: readonly IconSlot[] = Object.freeze(
  (Object.keys(ICON_SLOTS) as IconSlot[]).sort(),
);

/** Where a slot's generated art is expected to land. */
export const SLOT_ART_BASE = `${UI_ICON_BASE}slots/`;

export function isIconSlot(id: string): id is IconSlot {
  return Object.prototype.hasOwnProperty.call(ICON_SLOTS, id);
}

/** True when the slot has painted art in the build today (route 1). */
export function slotHasArt(slot: IconSlot): boolean {
  const art = (ICON_SLOTS[slot] as IconSlotSpec).art;
  return art !== undefined && hasIcon(art);
}

/** Slots still waiting for art — the generation plan's worklist. */
export function slotsMissingArt(): readonly IconSlot[] {
  return ICON_SLOT_IDS.filter((id) => !slotHasArt(id));
}

/** The URL route 1 or route 2 would load for a slot. */
export function slotUrl(slot: IconSlot): string {
  const spec = ICON_SLOTS[slot] as IconSlotSpec;
  if (spec.art !== undefined && hasIcon(spec.art)) return `${UI_ICON_BASE}${spec.art}.png`;
  return `${SLOT_ART_BASE}${slot}.png`;
}

/* ═══════════════════════════════════════════════ the once-per-session probe ══ */

const MISSING_KEY = 'rga.ui.slots.missing';
const missing = new Set<string>();
let missingLoaded = false;

function loadMissing(): void {
  if (missingLoaded) return;
  missingLoaded = true;
  try {
    const raw = globalThis.sessionStorage?.getItem(MISSING_KEY);
    if (raw) for (const id of raw.split(',')) if (id) missing.add(id);
  } catch {
    /* private mode: memory only */
  }
}

function rememberMissing(slot: string): void {
  missing.add(slot);
  try {
    globalThis.sessionStorage?.setItem(MISSING_KEY, [...missing].join(','));
  } catch {
    /* memory only */
  }
}

/** Test seam: forget every probe result. */
export function resetSlotProbesForTests(): void {
  missing.clear();
  missingLoaded = true;
  try {
    globalThis.sessionStorage?.removeItem(MISSING_KEY);
  } catch {
    /* nothing to clear */
  }
}

/* ════════════════════════════════════════════════════════ the placeholder ══ */

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Disc tone per group — muted enough that a placeholder never outshouts art. */
const GROUP_TONE: Readonly<Record<SlotGroup, readonly [string, string]>> = Object.freeze({
  nav: ['#5AB0E0', '#2E6F97'],
  header: ['#8FA3B8', '#4C5E72'],
  action: ['#FFD469', '#C98F1E'],
  currency: ['#FFD469', '#B9811A'],
  stat: ['#A8D8F0', '#3F7FA6'],
  // Palette tokens, not a flat slate grey (owner 2026-10-14: "no grey"). The
  // sky accent over the royal ribbon; the hex after each comma is the token's
  // own value, for a document without app.css (the admin, jsdom).
  status: ['var(--color-sky, #86BDE8)', 'var(--color-royal, #22417A)'],
  social: ['#7FD3A4', '#2F8457'],
  settings: ['#B7A6E8', '#5E4FA0'],
  match: ['#F2948A', '#A8433A'],
  commerce: ['#FFC46B', '#B5701C'],
});

let uid = 0;

/** The consistent stand-in: a toned disc, a heavy outline, a white pictogram. */
export function createSlotPlaceholder(doc: Document, slot: IconSlot, size = 20): SVGSVGElement {
  const spec = ICON_SLOTS[slot] as IconSlotSpec;
  const [light, dark] = GROUP_TONE[spec.group];
  const svg = doc.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', 'rga-slot rga-slot--placeholder');
  svg.setAttribute('data-slot', slot);

  const gid = `rga-slot-g${++uid}`;
  const defs = doc.createElementNS(SVG_NS, 'defs');
  const grad = doc.createElementNS(SVG_NS, 'linearGradient');
  grad.setAttribute('id', gid);
  grad.setAttribute('x1', '0');
  grad.setAttribute('y1', '0');
  grad.setAttribute('x2', '0');
  grad.setAttribute('y2', '1');
  for (const [offset, color] of [['0', light], ['1', dark]] as const) {
    const stop = doc.createElementNS(SVG_NS, 'stop');
    stop.setAttribute('offset', offset);
    // As a STYLE, not the presentation attribute: an SVG attribute cannot
    // resolve var(), a style declaration can.
    if (color.startsWith('var(')) stop.setAttribute('style', `stop-color:${color}`);
    else stop.setAttribute('stop-color', color);
    grad.append(stop);
  }
  defs.append(grad);
  svg.append(defs);

  const disc = doc.createElementNS(SVG_NS, 'circle');
  disc.setAttribute('cx', '16');
  disc.setAttribute('cy', '16');
  disc.setAttribute('r', '13.5');
  disc.setAttribute('fill', `url(#${gid})`);
  disc.setAttribute('stroke', '#0B0E13');
  disc.setAttribute('stroke-width', '2.5');
  svg.append(disc);

  // The one hard gloss highlight the painted set carries.
  const gloss = doc.createElementNS(SVG_NS, 'ellipse');
  gloss.setAttribute('cx', '12');
  gloss.setAttribute('cy', '9');
  gloss.setAttribute('rx', '5');
  gloss.setAttribute('ry', '2.2');
  gloss.setAttribute('fill', 'rgba(255,255,255,.45)');
  svg.append(gloss);

  const g = doc.createElementNS(SVG_NS, 'g');
  g.setAttribute('transform', 'translate(6.4 6.4) scale(.8)');
  g.setAttribute('fill', 'none');
  g.setAttribute('stroke-linecap', 'round');
  g.setAttribute('stroke-linejoin', 'round');
  for (const [stroke, width] of [['#0B0E13', '4.6'], ['#FFFFFF', '2.2']] as const) {
    const path = doc.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', PICTOGRAMS[spec.glyph]);
    path.setAttribute('stroke', stroke);
    path.setAttribute('stroke-width', width);
    g.append(path);
  }
  svg.append(g);
  return svg;
}

/* ═══════════════════════════════════════════════════════════ the factory ══ */

export interface SlotIconOptions {
  /** Rendered size in CSS px, square. Defaults to 20. */
  readonly size?: number;
  /** Extra classes on the outer element. */
  readonly class?: string;
}

/**
 * The icon for a slot — painted art, generated art, or the placeholder, in
 * that order. Always returns an element; always decorative.
 *
 * The returned node is a `<span class="rga-slot">` wrapper so the swap from a
 * failed `<img>` to the placeholder happens INSIDE it, and a caller holding
 * the reference never holds a detached node.
 */
export function createSlotIcon(doc: Document, slot: IconSlot, options: SlotIconOptions = {}): HTMLSpanElement {
  loadMissing();
  const size = options.size ?? 20;
  const wrap = doc.createElement('span');
  wrap.className = options.class === undefined ? 'rga-slot-icon' : `rga-slot-icon ${options.class}`;
  wrap.setAttribute('aria-hidden', 'true');
  wrap.setAttribute('data-icon-slot', slot);
  wrap.style.display = 'inline-flex';
  wrap.style.width = `${size}px`;
  wrap.style.height = `${size}px`;
  wrap.style.flex = '0 0 auto';
  wrap.style.alignItems = 'center';
  wrap.style.justifyContent = 'center';

  const placeholder = (): void => {
    wrap.replaceChildren(createSlotPlaceholder(doc, slot, size));
    wrap.setAttribute('data-icon-source', 'placeholder');
  };

  if (!slotHasArt(slot) && missing.has(slot)) {
    placeholder();
    return wrap;
  }

  const img = doc.createElement('img');
  img.className = 'rga-icon';
  img.src = slotUrl(slot);
  img.alt = '';
  img.width = size;
  img.height = size;
  img.decoding = 'async';
  img.draggable = false;
  img.style.width = `${size}px`;
  img.style.height = `${size}px`;
  img.style.objectFit = 'contain';
  img.addEventListener('error', () => {
    if (!slotHasArt(slot)) rememberMissing(slot);
    placeholder();
  });
  wrap.setAttribute('data-icon-source', slotHasArt(slot) ? 'art' : 'generated');
  wrap.append(img);
  return wrap;
}
